import { useState } from "react";
import { Edit2, Trash2, Plus, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog";
import {
  useCandidateProjects,
  useDeleteProject,
} from "@/pages/candidate-profile/hooks/useCandidateProfile";
import { ProjectFormModal } from "@/pages/profile/employee/sections/components/ProjectFormModal";
import type { ProfileDetail, ProjectDetail } from "@/interface/profile";
import type { SkillDetail } from "@/interface";
import { formatDate } from "@/utils/helper";

export function ProjectsSection({
  profileId,
}: {
  profileId?: number | string;
  profile?: ProfileDetail;
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<ProjectDetail | null>(
    null,
  );
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);

  const {
    data: candidateProjectsRes,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useCandidateProjects(profileId, { page: 1, page_size: 10 });
  const deleteMutation = useDeleteProject();

  const projects: ProjectDetail[] =
    candidateProjectsRes?.pages?.flatMap((page) =>
      Array.isArray(page?.data) ? page.data : page?.data?.results || [],
    ) || [];

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 50) {
      if (hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    }
  };

  const handleOpenAdd = () => {
    setSelectedProject(null);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (project: ProjectDetail) => {
    setSelectedProject(project);
    setIsModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTargetId) return;
    try {
      await deleteMutation.mutateAsync(deleteTargetId);
      setDeleteTargetId(null);
    } catch {
      // Error handled by mutation
    }
  };

  const getTechTags = (project: ProjectDetail): string[] => {
    if (!project) return [];
    if (Array.isArray(project.technologies)) {
      return project.technologies.map((t: SkillDetail) => t?.skill_name);
    }
    return [];
  };

  return (
    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-6 shadow-sm flex flex-col">
      <div className="flex justify-between items-center mb-6 border-b border-slate-100 dark:border-slate-800 pb-4 shrink-0">
        <div>
          <h2 className="text-lg font-bold text-primary">Projects</h2>
          <p className="text-xs text-slate-500">
            Candidate projects and contributions
          </p>
        </div>
        <Button
          onClick={handleOpenAdd}
          className="bg-[#1a0b2e] hover:bg-[#1a0b2e]/90 text-white text-xs h-8"
        >
          <Plus className="h-4 w-4 mr-2" /> Add Project
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center p-12">
          <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
        </div>
      ) : (
        <div
          onScroll={handleScroll}
          className="overflow-y-auto pr-1 flex-1 space-y-4 max-h-[500px]"
        >
          <div className="w-full overflow-x-auto border rounded-xl border-slate-200 dark:border-slate-800">
            <table className="w-full text-sm text-left">
              <thead className="bg-slate-50 dark:bg-slate-800/50 text-slate-500 border-b border-slate-200 dark:border-slate-800">
                <tr>
                  <th className="px-4 py-3 font-semibold">#</th>
                  <th className="px-4 py-3 font-semibold">Project Name</th>
                  <th className="px-4 py-3 font-semibold">
                    Client / Organization
                  </th>
                  <th className="px-4 py-3 font-semibold">Role</th>
                  <th className="px-4 py-3 font-semibold">Duration</th>
                  <th className="px-4 py-3 font-semibold">Technologies</th>
                  <th className="px-4 py-3 font-semibold text-right">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800/50">
                {projects.map((row: ProjectDetail, i: number) => {
                  const projectName = row.project_name || "Untitled Project";
                  const clientOrg = row.client_organization || "N/A";
                  const roleName = row.role || "Contributor";

                  const startDate = row.start_date
                    ? formatDate(row.start_date, "DD-MM-YYYY")
                    : "";
                  const endDate = row.is_current
                    ? "Present"
                    : row.end_date
                      ? formatDate(row.end_date, "DD-MM-YYYY")
                      : "";
                  const durationDisplay =
                    startDate || endDate
                      ? `${startDate || ""} ${startDate && endDate ? "-" : ""} ${endDate || ""}`
                      : "N/A";

                  const tags = getTechTags(row);

                  return (
                    <tr
                      key={row.id || i}
                      className="hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors"
                    >
                      <td className="px-4 py-3 text-slate-500">{i + 1}</td>
                      <td className="px-4 py-3 font-bold text-slate-900 dark:text-white">
                        {projectName}
                      </td>
                      <td className="px-4 py-3 text-slate-600 dark:text-slate-400">
                        {clientOrg}
                      </td>
                      <td className="px-4 py-3 text-slate-600 dark:text-slate-400">
                        {roleName}
                      </td>
                      <td className="px-4 py-3 text-slate-600 dark:text-slate-400">
                        {durationDisplay}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1.5 flex-wrap">
                          {tags.map((t, idx) => (
                            <span
                              key={idx}
                              className="px-2 py-0.5 rounded border border-slate-200 dark:border-slate-700 text-[10px] text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-slate-800"
                            >
                              {t}
                            </span>
                          ))}
                          {tags.length === 0 && (
                            <span className="text-[10px] text-slate-400 italic">
                              -
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => handleOpenEdit(row)}
                          className="p-1 hover:text-primary transition-colors mr-1"
                        >
                          <Edit2 className="h-3.5 w-3.5 text-slate-400" />
                        </button>
                        <button
                          onClick={() => setDeleteTargetId(String(row.id))}
                          className="p-1 hover:text-rose-500 transition-colors"
                        >
                          <Trash2 className="h-3.5 w-3.5 text-rose-400" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {projects.length === 0 && (
                  <tr>
                    <td
                      colSpan={7}
                      className="text-center py-8 text-slate-500 text-sm"
                    >
                      No projects added yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {isFetchingNextPage && (
            <div className="flex justify-center items-center py-4">
              <Loader2 className="w-5 h-5 animate-spin text-purple-600" />
              <span className="ml-2 text-xs text-slate-500 font-medium">
                Loading more projects...
              </span>
            </div>
          )}
        </div>
      )}

      <ProjectFormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        editData={selectedProject}
        profileId={profileId}
      />

      <ConfirmationDialog
        isOpen={Boolean(deleteTargetId)}
        onClose={() => setDeleteTargetId(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete Project"
        description="Are you sure you want to remove this project?"
        confirmText="Delete"
        confirmVariant="danger"
        isPending={deleteMutation.isPending}
      />
    </div>
  );
}
